import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Input, Button } from "reactstrap";
import {
  toBrandFormData,
  createBrandWithImage,
  updateBrandWithImage,
  deleteBrandImage,
  brandMenuUrl,
} from "../../utils/brandApi";
import { successToast, errorToast } from "../../shared/Toast";
import { slugError } from "../../constants/merchantPages";
import { C } from "../reporting/reportingStyles";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const inputStyle = { borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, padding: "8px 12px", color: C.text };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };
const hintStyle = { fontSize: 11, color: C.textLight, marginTop: 4 };

// One modal for create and edit.
//
// `row` : existing brand, or null to create
//
// Synced brands (those with a merchant_brand_id, i.e. pulled from Odoo's /pos/api/brands)
// have their English name locked: the next menu refresh overwrites it, so letting an admin
// type one here would just quietly lose the edit. Everything else on this form —
// Arabic name, logo, sort order, active — is admin-owned and the sync never touches it.
const BrandModal = ({ isOpen, toggle, onSaved, row, merchantId }) => {
  const editing = !!row;
  const isSynced = !!row?.merchant_brand_id;

  const [name, setName] = useState("");
  const [arabicName, setArabicName] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [active, setActive] = useState(true);
  const [slug, setSlug] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [removingImage, setRemovingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(row?.name || "");
    setArabicName(row?.arabicName || "");
    setSortOrder(row?.sortOrder != null ? String(row.sortOrder) : "0");
    setActive(row?.active ?? true);
    setSlug(row?.slug || "");
    setImageFile(null);
  }, [isOpen, row]);

  // Same rule as the merchant URL slug (constants/merchantPages.js, mirrored by the API's
  // src/util/slug.js). Optional: empty means "no public link".
  const slugProblem = slugError(slug, { required: false });
  const trimmedSlug = slug.trim();

  const handleRemoveImage = async () => {
    if (!row?.id || !row?.image) return;
    setRemovingImage(true);
    const result = await deleteBrandImage(row.id);
    setRemovingImage(false);
    if (result) {
      successToast("Logo removed");
      onSaved?.();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) return errorToast("Name is required");
    if (!editing && !merchantId) return errorToast("Select a merchant first");
    if (slugProblem) return errorToast(`Menu link slug: ${slugProblem}`);

    const fields = {
      name: name.trim(),
      arabicName: arabicName.trim(),
      sortOrder: Number(sortOrder) || 0,
      active,
      // Always sent, even empty: "" is how the API clears the slug and switches the link off.
      slug: trimmedSlug,
      ...(editing ? {} : { merchantId }),
    };

    // Always FormData: the endpoint sits behind the shared image-upload middleware, which
    // needs `dataType` to resolve its destination directory.
    const formData = toBrandFormData(fields, imageFile);

    setSaving(true);
    const result = editing
      ? await updateBrandWithImage(row.id, formData)
      : await createBrandWithImage(formData);
    setSaving(false);

    if (result) {
      successToast(`Brand ${editing ? "updated" : "created"}`);
      toggle();
      onSaved?.();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle} style={headerStyle}>
          {editing ? `Edit ${row?.name || "Brand"}` : "Add Brand"}
        </ModalHeader>
        <ModalBody style={bodyStyle}>
          {isSynced && (
            <div style={{
              background: C.coralLight, border: `1px solid ${C.coral}33`, borderRadius: 8,
              padding: "10px 12px", fontSize: 12, color: C.textMed, marginBottom: 16,
            }}>
              Synced from the merchant system (id <strong>{row.merchant_brand_id}</strong>).
              The English name is managed there and is overwritten on every menu refresh —
              the Arabic name, logo, sort order and active switch are yours.
            </div>
          )}

          <FormGroup>
            <label style={labelStyle}>Name (English)</label>
            <Input
              style={{ ...inputStyle, ...(isSynced ? { background: C.bg, color: C.textLight } : {}) }}
              value={name}
              disabled={isSynced}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Smash Burger"
            />
            {isSynced && <div style={hintStyle}>Managed by the merchant system.</div>}
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>
              Name (Arabic)<span style={{ color: C.textLight, fontWeight: 400 }}> — optional</span>
            </label>
            <Input
              style={{ ...inputStyle, direction: "rtl" }}
              value={arabicName}
              onChange={(e) => setArabicName(e.target.value)}
              placeholder="أدخل الاسم بالعربية"
            />
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>
              Menu link slug<span style={{ color: C.textLight, fontWeight: 400 }}> — optional</span>
            </label>
            <Input
              style={{ ...inputStyle, fontFamily: "monospace" }}
              value={slug}
              maxLength={60}
              placeholder="e.g. smash-burger"
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
            />
            {slugProblem ? (
              <div style={{ ...hintStyle, color: "#B42318" }}>{slugProblem}</div>
            ) : (
              <div style={hintStyle}>
                {trimmedSlug ? (
                  <>
                    Public menu page with only the branches tagged with this brand:{" "}
                    <code>{brandMenuUrl(row?.merchantReference || "<merchant-ref>", trimmedSlug)}</code>.
                    It works only while the brand is active, even if the merchant's own menu page is
                    switched off. Changing the slug breaks links already shared.
                  </>
                ) : (
                  "Set a slug to give this brand its own public menu link. Leave empty for no link."
                )}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>Logo</label>
            {row?.image && !imageFile && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <img
                  src={row.image}
                  alt={row.name}
                  style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white }}
                />
                <Button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={removingImage}
                  style={{ ...ghostBtnStyle, fontSize: 12, padding: "6px 12px" }}
                >
                  {removingImage ? "Removing…" : "Remove"}
                </Button>
              </div>
            )}
            <Input
              type="file"
              accept="image/*"
              style={inputStyle}
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>Sort order</label>
            <Input
              type="number"
              style={{ ...inputStyle, maxWidth: 140 }}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </FormGroup>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
            <Input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              style={{ position: "static", margin: 0 }}
            />
            Active
          </label>
          <div style={hintStyle}>
            Switching a brand off removes it from every app's brand filter on the next
            request. Its product and branch tags are kept, so it can be switched back on.
          </div>
        </ModalBody>
        <ModalFooter style={footerStyle}>
          <Button type="button" onClick={toggle} style={ghostBtnStyle}>Cancel</Button>
          <Button type="submit" disabled={saving} style={coralBtnStyle}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BrandModal;
